import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, ShieldCheck, Lock } from "lucide-react";

const DEMO = [
  ["Owner", "owner@metrocare.com"],
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, status } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (status === "authed") navigate("/portal/dashboard", { replace: true });
  }, [status, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name.split(" ")[0]}`);
    } else {
      toast.error(res.error);
    }
  };

  const quick = (em) => { setEmail(em); setPassword("Demo1234!"); };

  return (
    <div className="flex min-h-screen bg-slate-950" data-testid="login-page">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-slate-900 p-12 lg:flex">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #2563eb 0, transparent 45%), radial-gradient(circle at 80% 70%, #0f172a 0, transparent 40%)" }} />
        <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(#1e293b55 1px,transparent 1px),linear-gradient(90deg,#1e293b55 1px,transparent 1px)", backgroundSize: "40px 40px" }} />
        <Link to="/" className="relative"><Logo variant="light" /></Link>
        <div className="relative">
          <h2 className="font-display text-3xl font-bold leading-tight text-white">
            RideStamp Operations Portal
          </h2>
          <p className="mt-4 max-w-sm text-slate-400">
            Secure, role-based access for Owners, Supervisors, Dispatchers and Administrators.
            Every ride, driver and vehicle — accounted for.
          </p>
          <div className="mt-8 flex gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Role-based access</span>
            <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-blue-400" /> Tenant isolated</span>
          </div>
        </div>
        <div className="relative font-mono-data text-xs text-slate-600">SECURE PORTAL · AUTHORIZED USERS ONLY</div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-md">
          <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white" data-testid="back-to-site">
            <ArrowLeft className="h-4 w-4" /> Back to website
          </button>
          <div className="lg:hidden"><Logo variant="light" /></div>
          <h1 className="mt-6 font-display text-3xl font-bold text-white">Sign in</h1>
          <p className="mt-2 text-slate-400">Access the RideStamp operations portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" required data-testid="login-email"
                className="mt-1.5 border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 focus-visible:ring-blue-600" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required data-testid="login-password"
                className="mt-1.5 border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 focus-visible:ring-blue-600" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full gap-2 rounded-lg bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-500">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Sign in to portal
            </Button>
          </form>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Link to="/signup" data-testid="login-create-business"
              className="rounded-lg border border-blue-600/50 bg-blue-600/10 px-4 py-3 text-center text-sm font-semibold text-blue-300 transition-colors hover:bg-blue-600/20">
              Create business account
            </Link>
            <Link to="/join" data-testid="login-join-team"
              className="rounded-lg border border-slate-700 px-4 py-3 text-center text-sm font-semibold text-slate-300 transition-colors hover:border-slate-500 hover:text-white">
              Have an invite code? Join team
            </Link>
          </div>

          <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Demo account (password: Demo1234!)</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {DEMO.map(([role, em]) => (
                <button key={em} onClick={() => quick(em)} data-testid={`demo-${role.toLowerCase()}`}
                  className="rounded-lg border border-slate-700 bg-slate-800/50 px-3 py-2 text-left text-xs transition-colors hover:border-blue-600 hover:bg-slate-800">
                  <div className="font-semibold text-blue-400">{role}</div>
                  <div className="truncate text-slate-500">{em}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
